import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HOME } from "@/constants/testIds";
import Starfield from "@/components/Starfield";
import { apiClient } from "@/lib/api";

const HERO_BG = "/hero-heirloom.jpg";
const GALLERY_1 = "https://images.unsplash.com/photo-1593037029292-a850b2142c46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxkb2clMjBwZWFjZWZ1bCUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzgyMDI2ODg4fDA&ixlib=rb-4.1.0&q=85&w=1200";
const GALLERY_2 = "https://images.unsplash.com/photo-1576201836106-db1758fd1c97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxkb2clMjBwZWFjZWZ1bCUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzgyMDI2ODg4fDA&ixlib=rb-4.1.0&q=85&w=1200";
const RAINBOW_BG = "https://images.unsplash.com/photo-1578185926358-7e064647af0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxzdWJ0bGUlMjByYWluYm93JTIwc2t5fGVufDB8fHx8MTc4MjAyNjg4OHww&ixlib=rb-4.1.0&q=85&w=1600";

const MEMITO_SLUG = "c0ffeead-memito";

export default function Landing() {
  const [memito, setMemito] = useState(null);

  useEffect(() => {
    apiClient.get(`/memorials/${MEMITO_SLUG}`).then(({ data }) => setMemito(data)).catch(() => {});
  }, []);

  return (
    <>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden min-h-[88vh] flex items-center">
        {/* Background image — joyful spirit meadow */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          {/* Very gentle bottom-fade only; preserve the image's beauty */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FDFBF7]" />
        </div>
        <Starfield density={28} dustDensity={12} />

        <div className="relative z-20 w-full max-w-3xl mx-auto px-6">
          <div className="hero-card rounded-sm px-8 py-12 md:px-14 md:py-16 text-center">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-5">An Heirloom for the Heart · Est. 2026</div>
            <h1 className="font-serif-display text-[clamp(2rem,5vw,4rem)] leading-[1.06] text-[var(--rb-text-heading)]">
              Where they run free,<br/>
              <span className="italic">forever in </span>
              <span className="gold-text italic">golden meadows.</span>
            </h1>
            <p className="mt-6 max-w-xl mx-auto font-serif-display italic text-[17px] leading-relaxed text-[var(--rb-text-body)]">
              Handcrafted 3D dioramas, AI-painted sceneries, and verses written with love —
              keepsakes worthy of the companions who taught us how to love.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/create" className="btn-gold" data-testid={HOME.ctaCreateMemorial}>
                Create a memorial
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </Link>
              <Link to="/ecard" className="btn-ghost" data-testid={HOME.ctaSendEcard}>
                Send a sympathy eCard
              </Link>
            </div>
            <div className="mt-6">
              <Link to="/commission" className="text-[11px] tracking-[0.25em] uppercase text-[var(--rb-gold)] rainbow-underline hover:opacity-80 transition-opacity">
                Or commission a handcrafted diorama →
              </Link>
            </div>
            <p className="mt-5 text-[11px] tracking-[0.22em] uppercase text-[var(--rb-text-muted)]" data-testid="home-pricing-hint">
              Free 3-day preview ✦ Keep it forever for <span className="text-[var(--rb-gold)]">$29</span>
            </p>
            <div className="mt-8 ornament">
              <span>✦</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ INVITATION ============ */}
      <section className="relative max-w-6xl mx-auto px-6 md:px-12 py-28">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">Our Studio</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--rb-text-heading)] leading-tight">
              A memorial as singular<br/>as the love it holds.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[16px] leading-[1.8] text-[var(--rb-text-body)]">
            <p>
              Every diorama begins with a conversation — about your companion, their favorite hour of the day,
              the soft places they curled up to sleep. From there, we conjure a scene worthy of them:
              golden meadows under prismic skies, moonlit forests with quiet brooks, drifting clouds where
              they may run forever young.
            </p>
            <p>
              Each piece is finished with a verse written specifically for them, set in elegant typography,
              and sealed beneath glimmering glass — a small, sacred world to keep on your shelf.
            </p>
          </div>
        </div>
      </section>

      {/* ============ THREE PILLARS ============ */}
      <section className="relative py-24 bg-gradient-to-b from-transparent via-white/40 to-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-3">What you can do here</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--rb-text-heading)]">Three quiet gifts of remembrance</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                kicker: "I.",
                title: "Conjure their celestial scene",
                desc: "Describe their world and our AI painter renders a luminous, otherworldly scene — golden meadows, drifting clouds, soft rainbow skies — ready to become a 3D diorama or shareable keepsake.",
                cta: "Begin a memorial",
                to: "/create",
              },
              {
                kicker: "II.",
                title: "Receive their poem",
                desc: "Tell us a few cherished details. A literary verse arrives, written by Claude — gentle, dignified, and full of golden light. Yours to keep, frame, or whisper aloud.",
                cta: "Compose a verse",
                to: "/create",
              },
              {
                kicker: "III.",
                title: "Send sympathy, beautifully",
                desc: "An eCard worthy of the moment. Send a heartfelt note — by email or a private link — to someone who is grieving a beloved friend.",
                cta: "Open the card stationery",
                to: "/ecard",
              },
            ].map((p, i) => (
              <div key={i} className="glass p-10 rounded-sm hover:-translate-y-1 transition-all duration-500 relative">
                <div className="font-serif-display italic text-[var(--rb-gold)] text-2xl mb-2">{p.kicker}</div>
                <h3 className="font-serif-display text-2xl text-[var(--rb-text-heading)] leading-snug mb-4">{p.title}</h3>
                <p className="text-[15px] leading-[1.8] text-[var(--rb-text-body)] mb-6">{p.desc}</p>
                <Link to={p.to} className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[var(--rb-gold)] font-medium rainbow-underline">
                  {p.cta}
                  <span>→</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ GALLERY STRIP ============ */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Recent commissions</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--rb-text-heading)]">In quiet memory</h2>
            </div>
            <Link to="/gallery" className="btn-ghost">View the garden</Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[GALLERY_1, GALLERY_2].map((src, i) => (
              <div key={i} className="memorial-card group">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
                </div>
                <div className="p-10 text-center">
                  <div className="tracking-celestial text-[var(--rb-gold)] mb-3">In loving memory</div>
                  <div className="font-serif-display italic text-3xl text-[var(--rb-text-heading)]">
                    {i === 0 ? "Beloved Hazel" : "Sweet Biscuit"}
                  </div>
                  <div className="ornament mt-4"><span>✦</span></div>
                  <p className="font-serif-display italic text-[var(--rb-text-body)] mt-4 leading-relaxed">
                    {i === 0
                      ? "Your golden hour was every hour you were ours."
                      : "A small soft sun, who slept beside our heart."}
                  </p>
                </div>
              </div>
            ))}

            {/* Memito — real founding memorial (a cat), links to his page */}
            {memito ? (
              <Link to={`/memorial/${memito.share_slug}`} className="memorial-card group block" data-testid="landing-memito-card">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={`data:${memito.scenery_mime_type || "image/png"};base64,${memito.scenery_image_base64}`}
                    alt="In loving memory of Memito"
                    className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                  />
                </div>
                <div className="p-10 text-center">
                  <div className="tracking-celestial text-[var(--rb-gold)] mb-3">In loving memory</div>
                  <div className="font-serif-display italic text-3xl text-[var(--rb-text-heading)]">Memito</div>
                  <div className="ornament mt-4"><span>✦</span></div>
                  <p className="font-serif-display italic text-[var(--rb-text-body)] mt-4 leading-relaxed">
                    A little white daredevil who thought he was a small brave dog.
                  </p>
                </div>
              </Link>
            ) : (
              <div className="memorial-card">
                <div className="aspect-[4/3] bg-[var(--rb-gold-soft)] animate-pulse" />
                <div className="p-10 text-center">
                  <div className="h-3 bg-[var(--rb-gold-soft)] rounded w-24 mx-auto animate-pulse" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ CTA BANNER ============ */}
      <section className="relative py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden rounded-sm">
            <img src={RAINBOW_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/85" />
            <div className="relative z-10 p-16 md:p-24 text-center">
              <div className="tracking-celestial text-[var(--rb-gold)] mb-4">Freemium remembrance</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--rb-text-heading)] leading-tight">
                Try it free for three days —<br/>
                <span className="italic">keep it forever</span> for $29.
              </h2>
              <p className="font-serif-display italic text-[19px] text-[var(--rb-text-body)] mt-6 max-w-2xl mx-auto">
                Craft their full memorial — scenery, poem, eulogy — free for three days.
                Upgrade once and loved ones can gather here forever.
              </p>
              <div className="mt-10">
                <Link to="/create" className="btn-gold">Begin in quiet</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
